import {
  Attachments,
  Button,
  Dropdown,
  FormikSubmit,
  FormWrapper,
  ItemSplitter,
  OverflowContainer,
  PageContainer,
  routing,
  RowFlex,
  Table,
  TextField,
  theme,
  Toggle,
  useAxios,
  useCategories,
  useLoginContext,
  useModalContext,
} from "common";
import { Field, Formik } from "formik";
import { dissoc, uniq } from "ramda";
import { FaLink, FaPlus, FaTrash } from "react-icons/fa";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const ConvertConfirm = ({ sr, onCompleted }) => {
  const [duplicate, setDuplicate] = useState(null);

  const { callAxios, loading } = useAxios({
    alertSuccess: "Sourcing Request Converted to Brand Successfully!",
    alertError: false,
    onComplete: onCompleted,
    onError: (err) => {
      if (err?.response?.status === 409) {
        setDuplicate(err.response.data.existingBrandRequest);
      } else {
        alert(err?.message || "Something went wrong");
      }
    },
  });

  const callConvert = (action, existingBrandRequestId) => {
    const data = {};
    if (action) data.action = action;
    if (existingBrandRequestId) data.existingBrandRequestId = existingBrandRequestId;
    callAxios({ method: "POST", url: `/sourcingRequests/${sr.id}/convert`, data });
  };

  if (duplicate) {
    return (
      <OverflowContainer titleWithBodyAndActions>
        <h3>Duplicate Brand Request Found</h3>
        <p>
          A brand request named <strong>"{duplicate.brandName}"</strong> already exists with a
          similar name. What would you like to do?
        </p>
        <div style={{ marginTop: "20px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <Button onClick={() => callConvert("create")} loading={loading}>
            Create Anyway
          </Button>
          <Button
            onClick={() => callConvert("merge", duplicate._id)}
            loading={loading}
            style={{ backgroundColor: "#4caf50", color: "white" }}
          >
            Merge (Link to Existing)
          </Button>
          <Button secondary onClick={onCompleted}>
            Cancel
          </Button>
        </div>
      </OverflowContainer>
    );
  }

  return (
    <OverflowContainer titleWithBodyAndActions>
      <h3>Convert Sourcing Request to Brand</h3>
      <p>Are you sure you want to convert this sourcing request to a brand?</p>
      <div style={{ marginTop: "20px" }}>
        <Button onClick={() => callConvert()} loading={loading}>
          Convert
        </Button>
        <Button secondary onClick={onCompleted}>
          Cancel
        </Button>
      </div>
    </OverflowContainer>
  );
};

const SourcingRequestForm = ({ isEdit, formikSchema, refetch }) => {
  const navigate = useNavigate();
  const { currentUser } = useLoginContext();
  const { setModalContent, closeModal } = useModalContext();
  const goBack = () => navigate(routing.sourcingRequests.root);
  const isSimon = ["simon@hbamfl.com", "hurwitzse@gmail.com"].includes(
    currentUser.email,
  );
  const { callAxios, loading } = useAxios({
    alertSuccess: `Sourcing Request ${isEdit ? "Updated" : "Added"} Successfully!`,
    onComplete: (d) => {
      goBack();
    },
  });
  const { categories } = useCategories();

  return (
    <PageContainer>
      <h1>{isEdit ? "Update" : "Add"} Sourcing Request</h1>
      <Formik
        enableReinitialize
        {...formikSchema}
        onSubmit={(data) => {
          // The number inputs emit strings ("" when empty); coerce each
          // customer's annual projection to a number (or null) so saving an
          // empty dollar amount doesn't fail casting on the backend.
          const customers = (data.customers || []).map((c) => ({
            ...c,
            annualProjectionInDollars:
              c.annualProjectionInDollars === "" ||
              c.annualProjectionInDollars === null ||
              c.annualProjectionInDollars === undefined
                ? null
                : Number(c.annualProjectionInDollars),
          }));
          const sourcingRequest = {
            ...data,
            customers,
            // Keep the request-level projection in sync with the customers.
            annualProjectionInDollars: customers.reduce(
              (sum, c) => sum + (c.annualProjectionInDollars || 0),
              0,
            ),
          };
          callAxios({
            method: isEdit ? "PUT" : "POST",
            url: isEdit ? `/sourcingRequests/${data.id}` : "/sourcingRequests",
            data: dissoc("id", sourcingRequest),
          });
        }}
      >
        {({ values, setFieldValue }) => {
          const customerNames = values.customers.map((c) => c.name);
          const isDupe = uniq(customerNames).length !== customerNames.length;
          return (
            <FormWrapper>
              <ItemSplitter style={{ gridTemplateColumns: "1fr 1fr 2fr" }}>
                <RowFlex column>
                  <Field
                    name="name"
                    component={TextField}
                    label="Name"
                    required
                  />
                  <RowFlex>
                    <Field name="url" component={TextField} label="URL" />
                    {values.url && (
                      <Button
                        onClick={() => window.open(values.url, "_blank")}
                        style={{ marginTop: "20px", padding: "2px 2px", fontSize: "11px", height: "30px", width: "auto" }}
                      >
                        Open URL
                      </Button>
                    )}
                  </RowFlex>
                  <Field
                    name="category"
                    component={Dropdown}
                    label="Category"
                    options={categories}
                    required
                  />
                  <Field
                    name="notes"
                    component={TextField}
                    isArea
                    label="Notes"
                  />
                </RowFlex>
                <div>
                  <Attachments
                    url={isEdit ? `/sourcingRequests/${values.id}` : null}
                    autoUpload
                    showImmediately
                    attachments={values.attachments}
                    onComplete={(filename) => {
                      isEdit
                        ? refetch?.()
                        : setFieldValue("attachments", [
                            ...values.attachments,
                            filename,
                          ]);
                    }}
                  />
                </div>
                <OverflowContainer titleWithBody>
                  <RowFlex extend>
                    <h3>Customers</h3>{" "}
                    <FaPlus
                      cursor="pointer"
                      onClick={() =>
                        setFieldValue("customers", [
                          ...values.customers,
                          {
                            addedBy: currentUser.name,
                            name: "",
                            isCurrentlyBuying: false,
                            notes: "",
                            annualProjectionInDollars: null,
                          },
                        ])
                      }
                    />
                  </RowFlex>
                  <OverflowContainer>
                    <Table>
                      <thead>
                        <tr>
                          <th>Added By</th>
                          <th>Name</th>
                          <th>Buying</th>
                          <th>Annual Projection</th>
                          <th>Notes</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {values.customers.map((customer, index) => (
                          <tr key={index}>
                            <td>{customer.addedBy}</td>
                            <td>
                              <Field
                                name={`customers[${index}].name`}
                                component={TextField}
                                required
                              />
                            </td>
                            <td>
                              <Field
                                name={`customers[${index}].isCurrentlyBuying`}
                                component={Toggle}
                              />
                            </td>
                            <td>
                              <Field
                                name={`customers[${index}].annualProjectionInDollars`}
                                component={TextField}
                                type="number"
                                min="0"
                                prefix="$"
                              />
                            </td>
                            <td>
                              <Field
                                name={`customers[${index}].notes`}
                                component={TextField}
                              />
                            </td>
                            <td>
                              <FaTrash
                                color={theme.colors.danger}
                                style={{ cursor: values.customers.length <= 1 ? "not-allowed" : "pointer", opacity: values.customers.length <= 1 ? 0.3 : 1 }}
                                onClick={() => {
                                  if (values.customers.length <= 1) return;
                                  setFieldValue(
                                    "customers",
                                    values.customers.filter(
                                      (_, i) => i !== index,
                                    ),
                                  );
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </OverflowContainer>
                </OverflowContainer>
              </ItemSplitter>
              <div style={{ width: "300px" }}>
                {isEdit && formikSchema.initialValues.brandRequest && (
                  <Button
                    onClick={() => window.open(`/brand_requests/${formikSchema.initialValues.brandRequest.id}/view`, "_blank")}
                    style={{ marginBottom: "10px", backgroundColor: theme.colors.primary }}
                  >
                    See Brand Request
                  </Button>
                )}
                {isEdit && isSimon && formikSchema.initialValues.status === "Open" && (
                  <Button
                    onClick={() => {
                      setModalContent(
                        <ConvertConfirm
                          sr={formikSchema.initialValues}
                          onCompleted={() => {
                            closeModal();
                            goBack();
                          }}
                        />,
                      );
                    }}
                    style={{ marginBottom: "10px", backgroundColor: theme.colors.primary }}
                  >
                    Convert to Brand
                  </Button>
                )}
                <FormikSubmit loading={loading} disabled={isDupe}>
                  {isDupe
                    ? "Customers are Duplicate"
                    : isEdit
                      ? "Update"
                      : "Add"}
                </FormikSubmit>
                <Button secondary onClick={goBack}>
                  Cancel
                </Button>
              </div>
            </FormWrapper>
          );
        }}
      </Formik>
    </PageContainer>
  );
};

export default SourcingRequestForm;
